import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useLesson } from '@/api/useLessons';

function extractYouTubeEmbedUrl(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

const LessonDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const { data: lesson, isLoading } = useLesson(id);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(true);

  const handleStartQuiz = () => {
    if (id) navigate(isAdmin ? `/admin/quiz/${id}` : `/quiz/${id}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-[#4E7DF6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-gray-500">Lesson not found</p>
        <button onClick={() => navigate(-1)} className="text-[#4E7DF6] font-semibold">Go back</button>
      </div>
    );
  }

  const embedUrl = lesson.video_url ? extractYouTubeEmbedUrl(lesson.video_url) : null;

  return (
    <div className="flex flex-col gap-6 relative min-h-screen pb-24">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M15 10C15 10.4142 14.6642 10.75 14.25 10.75L7.61208 10.75L9.76983 12.7094C10.0684 12.9965 10.0777 13.4713 9.79062 13.7698C9.50353 14.0684 9.02875 14.0777 8.73017 13.7906L5.23017 10.5406C5.08311 10.3992 5 10.204 5 10C5 9.79599 5.08311 9.60078 5.23017 9.45938L8.73017 6.20938C9.02875 5.92228 9.50353 5.93159 9.79062 6.23017C10.0777 6.52875 10.0684 7.00353 9.76983 7.29063L7.61208 9.25L14.25 9.25C14.6642 9.25 15 9.58579 15 10Z" fill="#1F2937"/>
          </svg>
        </button>
        <div className="bg-[#6B8BF5] text-white px-6 py-2 rounded-full font-medium text-base">
          {lesson.title}
        </div>
      </div>

      {embedUrl && (
        <div>
          <div className="aspect-video w-full rounded-3xl overflow-hidden bg-black">
            <iframe
              width="100%"
              height="100%"
              src={embedUrl}
              title={lesson.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      <div 
        className={`fixed bottom-0 left-0 lg:left-[170px] right-0 lg:right-12 bg-white rounded-t-[32px] transition-transform duration-300 transform ${
          isDescriptionOpen ? 'translate-y-0' : 'translate-y-[calc(100%-120px)]'
        }`}
        style={{
          boxShadow: '0px -4px 24px rgba(0, 0, 0, 0.08)'
        }}
      >
        <div 
          className="flex flex-col items-center pt-3 pb-6 cursor-pointer"
          onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}
        >
          <div className="w-[116px] h-[9px] bg-gray-400 rounded-full" />
        </div>
        <div className="px-6 pb-[70px] space-y-4 text-gray-600">
          {lesson.content && (
            <div className="bg-[#4E7DF6] text-white rounded-lg p-4 whitespace-pre-wrap max-h-[200px] overflow-y-auto">
              {lesson.content}
            </div>
          )}

          {lesson.subject && (
            <div className="flex gap-2 text-sm text-gray-500">
              <span className="font-semibold">Subject:</span> {lesson.subject}
            </div>
          )}
          {lesson.class && (
            <div className="flex gap-2 text-sm text-gray-500">
              <span className="font-semibold">Class:</span> {lesson.class}
            </div>
          )}
          {lesson.language && (
            <div className="flex gap-2 text-sm text-gray-500">
              <span className="font-semibold">Language:</span> {lesson.language}
            </div>
          )}
          
          <button 
            onClick={handleStartQuiz}
            className="w-full mt-6 rounded-[120px] py-[9px] text-base px-6 bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-semibold transition-colors duration-200"
          >
            Start quiz
          </button>
        </div>
      </div>
    </div>
  );
};

export default LessonDetailsPage;